import {BitmapText, IBitmapTextStyle} from "pixi.js";
import {FrmShape} from "./FrmShape";
import {distance, EPS, FrmShapePoint} from "./FrmShapePoint";
import {FrmShapeConstraint} from "./FrmShapeConstraint";
import * as kiwi from '@lume/kiwi'
import {FrmVar} from "./FrmVar";
import {DimensionKind, DimensionSide} from "./FrmShapeDimension";

// MSDF bitmap font registered by DrawFrmApp.waitFonts(). color comes from the
// label's tint (set on creation), not the style.
const LABEL_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 10,
};

let debugId = 0;

export class FrmShapeLine {
    shape: FrmShape;
    pointA: FrmShapePoint;
    pointB: FrmShapePoint;

    id: number = debugId++;

    isFlex: boolean = true;
    cornerRadius: number = 0;

    // when true this line is not stroked — the path "lifts the pen" across it,
    // leaving a visible gap between the two segments it bridges. used for
    // profiles built from multiple disconnected pieces (e.g. the expansion
    // joint in FrameProfileEXP). the points still participate in the point
    // chain (so the pieces move together), they're just not drawn as a stroke.
    penUp: boolean = false;

    debugLabel: BitmapText | null = null;
    debugLabelText: string = "";

    kiwiVar: kiwi.Variable | null = null;

    constructor(shape: FrmShape, pointA: FrmShapePoint, pointB: FrmShapePoint) {
        this.shape = shape;
        this.pointA = pointA;
        this.pointB = pointB;
    }

    ensureDebugLabel(): BitmapText {
        if (this.debugLabel) return this.debugLabel;

        const t = new BitmapText(this.debugLabelText, LABEL_STYLE);
        t.anchor.set(0.5, 0.5);
        t.tint = 0x000000;
        this.shape.thing.graphic.addChild(t);
        this.debugLabel = t;

        return t;
    }

    get length(): number {
        return distance(this.pointA, this.pointB);
    }

    // flexLength allows the line to be flexible. by default lines are not flexible.
    flexLength(): void {
        this.isFlex = true;
    }

    // fixedLength fixes the length of the line to a particular value
    // (effectively queues up a dimension.updateLength for when shape.done is called)
    fixedLength(length: number, debugStr?: string): void {
        this.isFlex = false;
        this.shape.forceUpdateLength(this, length);
    }

    // dimMatches creates a rule where if this line is updated in length, the other is updated to be equivalent.
    // the reverse is also true.
    dimMatches(other: FrmShapeLine) {
        this.shape.constraints.push(new FrmShapeConstraint(this.shape, "equal-length", [this, other]));
        return this;
    }

    // setPenUp marks this line as a non-drawn bridge (see penUp). returns this
    // for chaining.
    setPenUp(): this {
        this.penUp = true;
        return this;
    }

    // setCornerRadius sets the radius of the rounded corner at this line's endpoint
    // (the corner where this line meets the next line in the shape).
    setCornerRadius(radius: number): this {
        this.cornerRadius = radius;
        return this;
    }

    // getEndpoint returns the endpoint of the line (the last point created)
    getEndpoint(): FrmShapePoint {
        return this.pointB;
    }

    isParallel(uvX: number, uvY: number): boolean {
        const dx = this.pointB.x - this.pointA.x;
        const dy = this.pointB.y - this.pointA.y;

        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < EPS) return false;

        // cross product of unit vectors; zero => parallel (same or opposite direction)
        return Math.abs((dx / len) * uvY - (dy / len) * uvX) < EPS;
    }

    // assumes A->B is parallel to uvX/Y; returns true when they point the same way
    directionMatches(uvX: number, uvY: number): boolean {
        const dx = this.pointB.x - this.pointA.x;
        const dy = this.pointB.y - this.pointA.y;
        return dx * uvX + dy * uvY > 0;
    }

    getStartpoint() {
        return this.pointA;
    }

    linkDimension(frmVar: FrmVar, kind: DimensionKind = "standard", side?: DimensionSide) {
        this.shape.addDimension(this.pointA, this.pointB, frmVar, kind, side);
        return this;
    }
}
